import React, { useContext, useState, useEffect } from 'react';
import AuthContext from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';
import axios from 'axios';
import {
    Box,
    Button,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Icon,
    Card,
    CardBody,
    Flex,
    SimpleGrid,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Badge,
    Stat,
    StatLabel,
    StatNumber,
    Progress,
    Tooltip,
    Grid,
    GridItem,
    Input,
    InputGroup,
    InputLeftElement,
    useBreakpointValue,
    useToast,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    IconButton,
    Collapse,
} from '@chakra-ui/react';
import {
    FaShieldAlt,
    FaSync,
    FaExclamationTriangle,
    FaCheckCircle,
    FaInfoCircle,
    FaMapMarkerAlt,
    FaUser,
    FaBrain,
    FaClock,
    FaFire,
    FaStar,
    FaChartLine,
    FaLightbulb,
    FaSearch,
} from 'react-icons/fa';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

function DashboardRMriskRanking() {
    const { token } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const [riskData, setRiskData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [expandedCustomer, setExpandedCustomer] = useState(null);
    const toast = useToast();

    const statSize = useBreakpointValue({ base: 'sm', md: 'md' });

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    const secondaryColor = themeData?.colors?.secondary || '#2C5282';
    const accentColor = themeData?.colors?.accent || '#63B3ED';
    const backgroundColor = themeData?.colors?.background || '#F7FAFC';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Card background - improved for dark theme
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';

    // Text colors with proper contrast - following Dashboard.js pattern
    const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';
    const strongTextColor = isDarkTheme ? '#F7FAFC' : 'gray.800';

    // Border colors
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const lightBorderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.2)' : 'gray.100';

    // Info card backgrounds - improved transparency for dark theme
    const actionBg = isDarkTheme ? 'rgba(99, 179, 237, 0.15)' : 'blue.50';
    const biasBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : accentColor + '10';

    // Placeholder/empty background
    const breakdownBg = isDarkTheme ? 'rgba(66, 153, 225, 0.12)' : 'gray.50';
    const nestedCardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : 'white';

    // Button hover
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;

    // Icon colors
    const mutedIconColor = isDarkTheme ? mutedTextColor : 'gray.400';

    // Tab colors
    const tabSelectedColor = isDarkTheme ? accentColor : primaryColor;

    const fetchRiskRanking = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/content/rm-risk-ranking`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setRiskData(response.data);
        } catch (err) {
            console.error('Error fetching risk ranking:', err);
            const errorMsg = err.response?.data?.message || 'Failed to fetch risk ranking';
            setError(errorMsg);
            toast({
                title: 'Error',
                description: errorMsg,
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchRiskRanking();
        }
        // eslint-disable-next-line
    }, [token]);

    const getRiskColor = (riskProfile) => {
        if (riskProfile <= 3) return 'green';
        if (riskProfile <= 6) return 'yellow';
        if (riskProfile <= 8) return 'orange';
        return 'red';
    };

    const getUrgencyColor = (urgencyLevel) => {
        switch (urgencyLevel?.toUpperCase()) {
            case 'HIGH': return 'red';
            case 'MEDIUM': return 'orange';
            case 'LOW': return 'green';
            default: return 'gray';
        }
    };

    const formatBiasName = (biasName) => {
        return biasName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const toggleCustomer = (customerId) => {
        if (expandedCustomer === customerId) {
            setExpandedCustomer(null);
        } else {
            setExpandedCustomer(customerId);
        }
    };

    const handleSearch = () => {
        // Search is handled by filtered arrays already
    };

    const highRisk = riskData?.high_risk || [];
    const mediumRisk = riskData?.medium_risk || [];
    const lowRisk = riskData?.low_risk || [];
    const totalCustomers = highRisk.length + mediumRisk.length + lowRisk.length;

    const filteredHighRisk = highRisk.filter(customer =>
        customer.customer_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.country_iso?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredMediumRisk = mediumRisk.filter(customer =>
        customer.customer_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.country_iso?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredLowRisk = lowRisk.filter(customer =>
        customer.customer_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.country_iso?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredTotalCustomers = filteredHighRisk.length + filteredMediumRisk.length + filteredLowRisk.length;

    // Customer Card Component
    const CustomerCard = ({ customer, categoryColor }) => {
        const isExpanded = expandedCustomer === customer.customer_id;

        return (
            <Card
                borderRadius="lg"
                boxShadow="sm"
                mb={3}
                borderLeft="4px solid"
                borderLeftColor={`${categoryColor}.500`}
                _hover={{ boxShadow: 'md' }}
                transition="all 0.2s"
                bg={cardBg}
                borderWidth="1px"
                borderColor={borderColor}
            >
                <CardBody>
                    <Flex justify="space-between" align="center">
                        <VStack align="start" spacing={2} flex="1">
                            <HStack spacing={3} flexWrap="wrap">
                                <Heading size="sm" color={headingTextColor}>
                                    {customer.customer_id}
                                </Heading>
                                <Badge
                                    colorScheme={getRiskColor(customer.risk_profile)}
                                    fontSize="xs"
                                    px={3}
                                    py={1}
                                    borderRadius="full"
                                    fontWeight="bold"
                                >
                                    Risk: {customer.risk_profile}/10
                                </Badge>
                                <Badge
                                    colorScheme={getUrgencyColor(customer.urgency_level)}
                                    fontSize="xs"
                                    px={3}
                                    py={1}
                                    borderRadius="full"
                                    fontWeight="bold"
                                >
                                    {customer.urgency_level}
                                </Badge>
                            </HStack>
                            <HStack spacing={4} fontSize="sm" color={bodyTextColor} flexWrap="wrap">
                                <HStack spacing={1}>
                                    <Icon as={FaStar} boxSize={3} color={isDarkTheme ? accentColor : primaryColor} />
                                    <Text fontWeight="semibold">{customer.priority_score?.toFixed(2)}</Text>
                                </HStack>
                                <HStack spacing={1}>
                                    <Icon as={FaMapMarkerAlt} boxSize={3} />
                                    <Text>{customer.country_iso}</Text>
                                </HStack>
                                <HStack spacing={1}>
                                    <Icon as={FaUser} boxSize={3} />
                                    <Text>{customer.age} years</Text>
                                </HStack>
                            </HStack>
                        </VStack>
                        <IconButton
                            icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            onClick={() => toggleCustomer(customer.customer_id)}
                            colorScheme="blue"
                            variant="ghost"
                            aria-label="Toggle details"
                        />
                    </Flex>

                    <Collapse in={isExpanded} animateOpacity>
                        <Box mt={6} pt={6} borderTop="1px solid" borderColor={borderColor}>
                            <VStack spacing={4} align="stretch">
                                {/* Recommended Action */}
                                {customer.recommended_action && (
                                    <Card 
                                        bg={actionBg} 
                                        borderRadius="md" 
                                        boxShadow="sm" 
                                        borderWidth="1px" 
                                        borderColor={borderColor}
                                    >
                                        <CardBody>
                                            <HStack spacing={2} mb={2}>
                                                <Icon as={FaLightbulb} color={isDarkTheme ? accentColor : accentColor} boxSize={5} />
                                                <Text fontWeight="bold" color={labelTextColor}>Recommended Action</Text>
                                            </HStack>
                                            <Text fontSize="sm" color={labelTextColor} lineHeight="1.6">
                                                {customer.recommended_action}
                                            </Text>
                                        </CardBody>
                                    </Card>
                                )}

                                {/* Top Biases */}
                                {customer.top_biases && customer.top_biases.length > 0 && (
                                    <Card 
                                        bg={biasBg} 
                                        borderRadius="md" 
                                        boxShadow="sm" 
                                        borderWidth="1px" 
                                        borderColor={borderColor}
                                    >
                                        <CardBody>
                                            <HStack spacing={2} mb={3}>
                                                <Icon as={FaBrain} color={isDarkTheme ? accentColor : primaryColor} boxSize={5} />
                                                <Text fontWeight="bold" color={labelTextColor}>Top Behavioral Biases</Text>
                                            </HStack>
                                            <VStack spacing={2} align="stretch">
                                                {customer.top_biases.map((bias, index) => (
                                                    <Box 
                                                        key={index} 
                                                        bg={nestedCardBg} 
                                                        p={3} 
                                                        borderRadius="md" 
                                                        boxShadow="sm" 
                                                        borderWidth="1px" 
                                                        borderColor={borderColor}
                                                    >
                                                        <Flex justify="space-between" align="center" mb={1}>
                                                            <Text fontSize="sm" fontWeight="semibold" color={labelTextColor}>
                                                                {formatBiasName(bias.bias_name)}
                                                            </Text>
                                                            <HStack spacing={2}>
                                                                <Badge colorScheme="purple" fontSize="xs">
                                                                    Score: {bias.score}
                                                                </Badge>
                                                                <Tooltip label="Confidence Level" placement="top">
                                                                    <Badge colorScheme="green" fontSize="xs">
                                                                        {(bias.confidence * 100).toFixed(0)}%
                                                                    </Badge>
                                                                </Tooltip>
                                                            </HStack>
                                                        </Flex>
                                                        <Progress
                                                            value={bias.score}
                                                            max={100}
                                                            size="sm"
                                                            colorScheme={bias.score > 80 ? 'red' : bias.score > 60 ? 'orange' : 'yellow'}
                                                            borderRadius="full"
                                                        />
                                                    </Box>
                                                ))}
                                            </VStack>
                                        </CardBody>
                                    </Card>
                                )}

                                {/* Breakdown Metrics */}
                                {customer.breakdown && (
                                    <Card 
                                        bg={breakdownBg} 
                                        borderRadius="md" 
                                        boxShadow="sm" 
                                        borderWidth="1px" 
                                        borderColor={borderColor}
                                    >
                                        <CardBody>
                                            <HStack spacing={2} mb={3}>
                                                <Icon as={FaChartLine} color="orange.500" boxSize={5} />
                                                <Text fontWeight="bold" color={labelTextColor}>Risk Breakdown</Text>
                                            </HStack>
                                            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
                                                <GridItem>
                                                    <Box 
                                                        bg={nestedCardBg} 
                                                        p={3} 
                                                        borderRadius="md" 
                                                        boxShadow="sm" 
                                                        borderWidth="1px" 
                                                        borderColor={borderColor}
                                                    >
                                                        <HStack justify="space-between" mb={1}>
                                                            <HStack>
                                                                <Icon as={FaFire} boxSize={3} color="red.400" />
                                                                <Text fontSize="xs" color={bodyTextColor}>Bias Severity</Text>
                                                            </HStack>
                                                            <Text fontSize="sm" fontWeight="bold" color={strongTextColor}>
                                                                {customer.breakdown.bias_severity?.toFixed(1)}
                                                            </Text>
                                                        </HStack>
                                                        <Progress
                                                            value={customer.breakdown.bias_severity * 10}
                                                            size="xs"
                                                            colorScheme="red"
                                                            borderRadius="full"
                                                        />
                                                    </Box>
                                                </GridItem>

                                                <GridItem>
                                                    <Box 
                                                        bg={nestedCardBg} 
                                                        p={3} 
                                                        borderRadius="md" 
                                                        boxShadow="sm" 
                                                        borderWidth="1px" 
                                                        borderColor={borderColor}
                                                    >
                                                        <HStack justify="space-between" mb={1}>
                                                            <HStack>
                                                                <Icon as={FaExclamationTriangle} boxSize={3} color="orange.400" />
                                                                <Text fontSize="xs" color={bodyTextColor}>Risk Urgency</Text>
                                                            </HStack>
                                                            <Text fontSize="sm" fontWeight="bold" color={strongTextColor}>
                                                                {customer.breakdown.risk_urgency?.toFixed(2)}
                                                            </Text>
                                                        </HStack>
                                                        <Progress
                                                            value={customer.breakdown.risk_urgency * 10}
                                                            size="xs"
                                                            colorScheme="orange"
                                                            borderRadius="full"
                                                        />
                                                    </Box>
                                                </GridItem>

                                                <GridItem>
                                                    <Box 
                                                        bg={nestedCardBg} 
                                                        p={3} 
                                                        borderRadius="md" 
                                                        boxShadow="sm" 
                                                        borderWidth="1px" 
                                                        borderColor={borderColor}
                                                    >
                                                        <HStack justify="space-between" mb={1}>
                                                            <HStack>
                                                                <Icon as={FaClock} boxSize={3} color="blue.400" />
                                                                <Text fontSize="xs" color={bodyTextColor}>Time Sensitivity</Text>
                                                            </HStack>
                                                            <Text fontSize="sm" fontWeight="bold" color={strongTextColor}>
                                                                {customer.breakdown.time_sensitivity?.toFixed(2)}
                                                            </Text>
                                                        </HStack>
                                                        <Progress
                                                            value={customer.breakdown.time_sensitivity * 10}
                                                            size="xs"
                                                            colorScheme="blue"
                                                            borderRadius="full"
                                                        />
                                                    </Box>
                                                </GridItem>

                                                <GridItem>
                                                    <Box 
                                                        bg={nestedCardBg} 
                                                        p={3} 
                                                        borderRadius="md" 
                                                        boxShadow="sm" 
                                                        borderWidth="1px" 
                                                        borderColor={borderColor}
                                                    >
                                                        <HStack justify="space-between" mb={1}>
                                                            <HStack>
                                                                <Icon as={FaStar} boxSize={3} color={isDarkTheme ? accentColor : primaryColor} />
                                                                <Text fontSize="xs" color={bodyTextColor}>Customer Value</Text>
                                                            </HStack>
                                                            <Text fontSize="sm" fontWeight="bold" color={strongTextColor}>
                                                                {customer.breakdown.customer_value?.toFixed(2)}
                                                            </Text>
                                                        </HStack>
                                                        <Progress
                                                            value={customer.breakdown.customer_value * 10}
                                                            size="xs"
                                                            colorScheme="purple"
                                                            borderRadius="full"
                                                        />
                                                    </Box>
                                                </GridItem>
                                            </Grid>
                                        </CardBody>
                                    </Card>
                                )}
                            </VStack>
                        </Box>
                    </Collapse>
                </CardBody>
            </Card>
        );
    };

    return (
        <Box minH="100vh" width="100%" pb={{ base: '90px', md: '20px' }} bg={backgroundColor}>
            <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
                <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                    {/* Header */}
                    <Box textAlign="center" pt={{ base: 2, md: 4 }}>
                        <Heading
                            size={{ base: 'lg', md: 'xl' }}
                            bgGradient={isDarkTheme 
                                ? `linear(to-r, ${accentColor}, ${primaryColor})` 
                                : `linear(to-r, ${primaryColor}, ${accentColor})`
                            }
                            bgClip="text"
                            mb={2}
                            fontWeight="extrabold"
                        >
                            <HStack justify="center" spacing={2}>
                                <Icon as={FaShieldAlt} boxSize={{ base: 6, md: 8 }} />
                                <Text>Risk Ranking Dashboard</Text>
                            </HStack>
                        </Heading>
                        <Text color={bodyTextColor} fontSize={{ base: 'sm', md: 'md' }} px={{ base: 4, md: 0 }}>
                            Customers categorized by risk level for strategic portfolio management
                        </Text>
                    </Box>

                    {/* Statistics Cards - Clickable */}
                    {!loading && riskData && (
                        <SimpleGrid columns={{ base: 3, md: 3 }} spacing={{ base: 3, md: 4 }}>
                            <Card
                                bg={cardBg}
                                borderRadius="xl"
                                boxShadow="sm"
                                border="2px"
                                borderColor={activeTab === 0 ? 'red.500' : lightBorderColor}
                                cursor="pointer"
                                onClick={() => setActiveTab(0)}
                                _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
                                transition="all 0.2s"
                            >
                                <CardBody p={{ base: 4, md: 5 }}>
                                    <Stat size={statSize}>
                                        <HStack spacing={2} mb={1}>
                                            <Icon as={FaExclamationTriangle} color="red.500" boxSize={{ base: 4, md: 5 }} />
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                High Risk
                                            </StatLabel>
                                        </HStack>
                                        <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="red.600" fontWeight="bold">
                                            {highRisk.length}
                                        </StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>

                            <Card
                                bg={cardBg}
                                borderRadius="xl"
                                boxShadow="sm"
                                border="2px"
                                borderColor={activeTab === 1 ? 'orange.500' : lightBorderColor}
                                cursor="pointer"
                                onClick={() => setActiveTab(1)}
                                _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
                                transition="all 0.2s"
                            >
                                <CardBody p={{ base: 4, md: 5 }}>
                                    <Stat size={statSize}>
                                        <HStack spacing={2} mb={1}>
                                            <Icon as={FaInfoCircle} color="orange.500" boxSize={{ base: 4, md: 5 }} />
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                Medium Risk
                                            </StatLabel>
                                        </HStack>
                                        <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="orange.600" fontWeight="bold">
                                            {mediumRisk.length}
                                        </StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>

                            <Card
                                bg={cardBg}
                                borderRadius="xl"
                                boxShadow="sm"
                                border="2px"
                                borderColor={activeTab === 2 ? 'green.500' : lightBorderColor}
                                cursor="pointer"
                                onClick={() => setActiveTab(2)}
                                _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
                                transition="all 0.2s"
                            >
                                <CardBody p={{ base: 4, md: 5 }}>
                                    <Stat size={statSize}>
                                        <HStack spacing={2} mb={1}>
                                            <Icon as={FaCheckCircle} color="green.500" boxSize={{ base: 4, md: 5 }} />
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                Low Risk
                                            </StatLabel>
                                        </HStack>
                                        <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="green.600" fontWeight="bold">
                                            {lowRisk.length}
                                        </StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>
                        </SimpleGrid>
                    )}

                    {/* Error Alert */}
                    {error && (
                        <Alert 
                            status="error" 
                            borderRadius="xl" 
                            variant="left-accent" 
                            boxShadow="sm" 
                            bg={cardBg}
                            borderWidth="1px"
                            borderColor={borderColor}
                        >
                            <AlertIcon />
                            <Box flex="1">
                                <AlertTitle fontSize={{ base: 'sm', md: 'md' }} color={headingTextColor}>Error!</AlertTitle>
                                <AlertDescription fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>{error}</AlertDescription>
                            </Box>
                        </Alert>
                    )}

                    {/* Search and Refresh */}
                    <Card 
                        bg={cardBg} 
                        borderRadius="lg" 
                        boxShadow="md" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody>
                            <HStack spacing={4}>
                                <InputGroup flex={1}>
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={FaSearch} color={mutedIconColor} />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search by Customer ID or Country..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        borderColor={borderColor}
                                        _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                        bg={isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : cardBg}
                                        color={bodyTextColor}
                                    />
                                </InputGroup>
                                <Button
                                    leftIcon={<FaSync />}
                                    bg={primaryColor}
                                    color="white"
                                    onClick={fetchRiskRanking}
                                    isLoading={loading}
                                    _hover={{ bg: buttonHoverBg }}
                                >
                                    Refresh
                                </Button>
                            </HStack>
                        </CardBody>
                    </Card>

                    {/* Tabbed Customer Lists */}
                    {loading ? (
                        <Flex justify="center" align="center" py={20}>
                            <VStack spacing={4}>
                                <Spinner size="xl" color={primaryColor} thickness="4px" />
                                <Text color={mutedTextColor} fontSize="sm">Loading risk rankings...</Text>
                            </VStack>
                        </Flex>
                    ) : filteredTotalCustomers > 0 || totalCustomers > 0 ? (
                        <Tabs index={activeTab} onChange={setActiveTab} colorScheme="purple">
                            <TabList>
                                <Tab 
                                    _selected={{ 
                                        color: tabSelectedColor, 
                                        borderColor: tabSelectedColor 
                                    }}
                                    color={bodyTextColor}
                                >
                                    <HStack spacing={2}>
                                        <Icon as={FaExclamationTriangle} color="red.500" />
                                        <Text>High Risk</Text>
                                        <Badge colorScheme="red" borderRadius="full">
                                            {filteredHighRisk.length}
                                        </Badge>
                                    </HStack>
                                </Tab>
                                <Tab 
                                    _selected={{ 
                                        color: tabSelectedColor, 
                                        borderColor: tabSelectedColor 
                                    }}
                                    color={bodyTextColor}
                                >
                                    <HStack spacing={2}>
                                        <Icon as={FaInfoCircle} color="orange.500" />
                                        <Text>Medium Risk</Text>
                                        <Badge colorScheme="orange" borderRadius="full">
                                            {filteredMediumRisk.length}
                                        </Badge>
                                    </HStack>
                                </Tab>
                                <Tab 
                                    _selected={{ 
                                        color: tabSelectedColor, 
                                        borderColor: tabSelectedColor 
                                    }}
                                    color={bodyTextColor}
                                >
                                    <HStack spacing={2}>
                                        <Icon as={FaCheckCircle} color="green.500" />
                                        <Text>Low Risk</Text>
                                        <Badge colorScheme="green" borderRadius="full">
                                            {filteredLowRisk.length}
                                        </Badge>
                                    </HStack>
                                </Tab>
                            </TabList>

                            <TabPanels>
                                {/* High Risk Panel */}
                                <TabPanel px={0} pt={6}>
                                    {filteredHighRisk.length > 0 ? (
                                        <VStack spacing={0} align="stretch">
                                            {filteredHighRisk.map((customer, index) => (
                                                <CustomerCard
                                                    key={customer.customer_id || index}
                                                    customer={customer}
                                                    categoryColor="red"
                                                />
                                            ))}
                                        </VStack>
                                    ) : (
                                        <Box textAlign="center" py={10}>
                                            <Icon as={searchTerm ? FaSearch : FaCheckCircle} boxSize={12} color={mutedIconColor} mb={4} />
                                            <Text color={mutedTextColor} fontSize="lg" fontWeight="medium">
                                                {searchTerm ? `No high risk customers found matching "${searchTerm}"` : 'No high risk customers'}
                                            </Text>
                                            {searchTerm && (
                                                <Button
                                                    mt={4}
                                                    variant="link"
                                                    color={isDarkTheme ? accentColor : primaryColor}
                                                    onClick={() => setSearchTerm('')}
                                                >
                                                    Clear search
                                                </Button>
                                            )}
                                        </Box>
                                    )}
                                </TabPanel>

                                {/* Medium Risk Panel */}
                                <TabPanel px={0} pt={6}>
                                    {filteredMediumRisk.length > 0 ? (
                                        <VStack spacing={0} align="stretch">
                                            {filteredMediumRisk.map((customer, index) => (
                                                <CustomerCard
                                                    key={customer.customer_id || index}
                                                    customer={customer}
                                                    categoryColor="orange"
                                                />
                                            ))}
                                        </VStack>
                                    ) : (
                                        <Box textAlign="center" py={10}>
                                            <Icon as={searchTerm ? FaSearch : FaCheckCircle} boxSize={12} color={mutedIconColor} mb={4} />
                                            <Text color={mutedTextColor} fontSize="lg" fontWeight="medium">
                                                {searchTerm ? `No medium risk customers found matching "${searchTerm}"` : 'No medium risk customers'}
                                            </Text>
                                            {searchTerm && (
                                                <Button
                                                    mt={4}
                                                    variant="link"
                                                    color={isDarkTheme ? accentColor : primaryColor}
                                                    onClick={() => setSearchTerm('')}
                                                >
                                                    Clear search
                                                </Button>
                                            )}
                                        </Box>
                                    )}
                                </TabPanel>

                                {/* Low Risk Panel */}
                                <TabPanel px={0} pt={6}>
                                    {filteredLowRisk.length > 0 ? (
                                        <VStack spacing={0} align="stretch">
                                            {filteredLowRisk.map((customer, index) => (
                                                <CustomerCard
                                                    key={customer.customer_id || index}
                                                    customer={customer}
                                                    categoryColor="green"
                                                />
                                            ))}
                                        </VStack>
                                    ) : (
                                        <Box textAlign="center" py={10}>
                                            <Icon as={searchTerm ? FaSearch : FaCheckCircle} boxSize={12} color={mutedIconColor} mb={4} />
                                            <Text color={mutedTextColor} fontSize="lg" fontWeight="medium">
                                                {searchTerm ? `No low risk customers found matching "${searchTerm}"` : 'No low risk customers'}
                                            </Text>
                                            {searchTerm && (
                                                <Button
                                                    mt={4}
                                                    variant="link"
                                                    color={isDarkTheme ? accentColor : primaryColor}
                                                    onClick={() => setSearchTerm('')}
                                                >
                                                    Clear search
                                                </Button>
                                            )}
                                        </Box>
                                    )}
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    ) : (
                        <Card 
                            borderRadius="lg" 
                            boxShadow="md" 
                            bg={cardBg} 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <Box textAlign="center" py={10}>
                                    <Icon as={FaShieldAlt} boxSize={{ base: 10, md: 12 }} color={mutedIconColor} mb={4} />
                                    <Text color={mutedTextColor} fontSize={{ base: 'md', md: 'lg' }} fontWeight="medium">
                                        No risk ranking data available
                                    </Text>
                                    <Text color={mutedTextColor} fontSize="sm" mt={2}>
                                        Customer risk rankings will appear here once analyzed
                                    </Text>
                                </Box>
                            </CardBody>
                        </Card>
                    )}
                </VStack>
            </Container>
        </Box>
    );
}

export default DashboardRMriskRanking;